<script setup lang="ts">
import startLogo from '@/assets/images/ide/start-logo.svg'
import sparkle from '@/assets/images/ide/sparkle.svg'
import sparkles from '@/assets/images/ide/ai/sparkles.svg?component'

import { AITypeList, type IAIType, ChatStatus, type IChat, AIType } from '@/utils/jDroid'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'

import { computed } from 'vue'
import { useField, useForm } from 'vee-validate'
import { string } from 'yup'
import { isEmpty } from 'lodash-es'

import jdroidService from '@/services/ide/jdroid.service'
import { useAuthStore } from '@/stores/auth.store'
import { useJdroidStore } from '@/stores/jdroid.store'
import { useTabStore } from '@/stores/tabs.store'
import { useIdeStore } from '@/stores/ide.store'
import editorService from '@/services/ide/editor.service'

const props = defineProps({
  currentPosition: {
    type: String,
    required: false
  }
})

const authStore = useAuthStore()
const jdroidStore = useJdroidStore()
const tabStore = useTabStore()
const ideStore = useIdeStore()

const { handleSubmit, resetForm } = useForm()

const { value: prompt } = useField<string>('prompt', string().required())

const isLastChatLoading = computed(() => {
  return jdroidStore.isLastChatLoading
})

const firstName = computed(() => authStore.firstName)

const largeScreen = computed(() => {
  return (
    (tabStore.tabWidthCurrentSize.large &&
      tabStore.tabWidthCurrentSize.tabName == TAB_ICONS_NAMES.JDROID) ||
    (tabStore.tabWidthCurrentSizeBottom.large &&
      tabStore.tabWidthCurrentSizeBottom.tabName == TAB_ICONS_NAMES.JDROID)
  )
})

const onSubmit = handleSubmit(async (values) => {
  const chat: IChat = {
    type: AIType.CHAT,
    autoReplaceEditor: false,
    prompt: values.prompt,
    ideContent: null,
    outputContent: null,
    header: 'Genarate Code',
    status: ChatStatus.LOADING,
    response: null
  }

  if (!isEmpty(prompt.value.trim())) {
    jdroidService.addChat(chat)
    if (authStore.isUserloggedIn) resetForm()
  }
})
/**
 * @param event
 * checks the enter and shift key pressed together, if not calls submit
 */
const onEnterChatBox = (event: any) => {
  if (!event.shiftKey) onSubmit()
}
/**
 * Handles the AI type
 * @param type IAIType
 */
const handleAi = async (type: IAIType) => {
  await new Promise((resolve) => setTimeout(resolve, 100))
  editorService.resizeCodeEditor()
  const chat: IChat = {
    type: type.type,
    autoReplaceEditor: false,
    ideContent: null,
    outputContent: null,
    header: type.header,
    status: ChatStatus.LOADING,
    response: null
  }
  jdroidService.addChat(chat)
}
</script>

<template>
  <div class="surface-secondary-base flex h-full w-full flex-col gap-8 overflow-x-scroll">
    <div
      class="flex items-center justify-start px-5 pt-6"
      :class="[props.currentPosition === TAB_BUTTON_POSITION.BOTTOM ? 'gap-4 pt-4' : 'gap-4']"
    >
      <img :src="startLogo" class="hidden h-20 w-24 sm:flex" />
      <div
        v-if="props.currentPosition === TAB_BUTTON_POSITION.RIGHT"
        class="gradient-text flex w-fit flex-col flex-wrap text-[28px] font-semibold"
      >
        <p>
          Hello<span v-show="firstName" class="ml-2 inline-block">{{ firstName }}</span
          >,
        </p>
        <p>What should we build today?</p>
      </div>
      <div v-else class="gradient-text flex w-fit flex-col flex-wrap text-[28px] font-semibold">
        <p>
          Hello<span v-show="firstName" class="ml-2 inline-block">{{ firstName }}</span
          >, What should we build today?
        </p>
      </div>
    </div>
    <div class="flex w-full flex-col gap-2 px-2">
      <h5 class="text-xs font-medium leading-4">What should we build today?</h5>
      <form class="flex w-full flex-col gap-3">
        <div class="relative w-full">
          <sparkles class="icon-ide-emphasized absolute left-2 top-3 h-4 w-4" />
          <textarea
            id="ide-jdroid-user-input"
            class="stroke-primary-default background-primary block w-full resize-none rounded-md border pl-7 pr-9 pt-3 text-xs font-normal leading-4 outline-none placeholder:text-gray-600 dark:placeholder:text-gray-200 dark:focus:text-white"
            v-model="prompt"
            placeholder="Generate a prompt or ask question to JDroid here..."
            :rows="props.currentPosition !== TAB_BUTTON_POSITION.BOTTOM ? '12' : '4'"
            @keyup.enter="onEnterChatBox"
          ></textarea>
        </div>
      </form>
    </div>

    <div class="flex w-full flex-col gap-2 px-2 pb-4">
      <h5 class="pb-2 text-xs font-medium leading-4">Explore full coding abilities of JDroid</h5>
      <div
        class="flex"
        :class="[
          !largeScreen ||
          props.currentPosition === TAB_BUTTON_POSITION.RIGHT ||
          ideStore.currentMobileTab
            ? 'flex-col gap-7'
            : 'flex-row gap-3'
        ]"
      >
        <button
          v-for="(type, index) of AITypeList"
          :id="type?.ideWelcomeId"
          :key="index"
          :disabled="isLastChatLoading"
          class="background-primary flex w-full items-start gap-2 rounded-lg px-2 py-3"
          @click="handleAi(type)"
        >
          <img :src="sparkle" class="h-5 w-5" alt="JDroid" />
          <div class="flex flex-col items-start gap-1">
            <div class="text-high text-xs font-medium">{{ type.header }}</div>
            <div class="text-low text-start text-[10px] font-light">{{ type.description }}</div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>
